import api from './api';

/**
 * シフト管理のAPI。
 *
 * ## プランと権限は別
 *
 * - `PLAN_REQUIRED`         … 会社の契約に含まれていない（上位プランの案内を出す）
 * - `SHIFT_MANAGE_REQUIRED` … 契約はあるが、その人に編集権限が無い
 *
 * **画面で隠すのは案内であって防御ではない。** 判定はサーバが持っている。
 */

export interface ShiftPattern {
  id: number;
  name: string;
  shortLabel: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  color: string | null;
  displayOrder: number;
  isActive: boolean;
}

/** シフト1枠。公休は `isDayOff = true` で時刻を持たない */
export interface Shift {
  id: number;
  userId: number;
  date: string;
  isDayOff: boolean;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  patternId: number | null;
  note: string | null;
  publishedAt: string | null;
}

/** 保存時に送る枠。id は持たない（その日を丸ごと置き換えるため） */
export interface ShiftSlotInput {
  isDayOff: boolean;
  startTime?: string | null;
  endTime?: string | null;
  breakMinutes?: number;
  patternId?: number | null;
  note?: string | null;
}

export const listShiftPatterns = async (includeInactive = false): Promise<ShiftPattern[]> => {
  const r = await api.get('/shift-patterns', { params: { includeInactive } });
  return r.data.patterns;
};

export const createShiftPattern = async (p: Partial<ShiftPattern>): Promise<ShiftPattern> => {
  const r = await api.post('/shift-patterns', p);
  return r.data.pattern;
};

export const updateShiftPattern = async (id: number, p: Partial<ShiftPattern>): Promise<ShiftPattern> => {
  const r = await api.put(`/shift-patterns/${id}`, p);
  return r.data.pattern;
};

/** 消さずに無効にする。過去のシフトが参照しているため */
export const deactivateShiftPattern = async (id: number): Promise<void> => {
  await api.delete(`/shift-patterns/${id}`);
};

/**
 * 期間のシフトを取る。**一度に扱えるのは62日まで**（サーバ側の上限）。
 * 返る `scope` が `self` なら、自分の公開済みしか入っていない。
 */
/**
 * シフトの一覧。
 *
 * ⚠️ **「自分のシフト」は `mine: true` で呼ぶ。** 権限に任せると、組む側の
 * 権限を持つ人(店長)の画面に**他人のシフトが自分のものとして並ぶ**
 * (代表指摘 2026-09-02)。`mine` を付けると誰が呼んでも
 * 自分のぶん・公開済みだけが返る。
 */
export const listShifts = async (
  from: string,
  to: string,
  opts?: { mine?: boolean }
): Promise<{ shifts: Shift[]; scope: 'self' | 'company' }> => {
  const r = await api.get('/shifts', { params: { from, to, ...(opts?.mine ? { mine: 1 } : {}) } });
  return r.data;
};

/**
 * 1人の1日ぶんを**丸ごと置き換える**。
 *
 * 差分ではなく置換なのは、中抜けで枠が2つある日を1回で表すため。
 * `slots: []` を渡すとその日は「未定」に戻る。
 */
export const saveDayShifts = async (
  userId: number,
  date: string,
  slots: ShiftSlotInput[],
  /**
   * 公開したまま**その日だけ開けて直す**とき true。
   * サーバは公開状態を保ったまま入れ替え、履歴に残す。
   * **過ぎた日は通らない**（`409 PAST_LOCKED`）。
   */
  editPublished?: boolean
): Promise<Shift[]> => {
  const r = await api.put('/shifts', { userId, date, slots, editPublished });
  return r.data.shifts;
};

/**
 * 受付期間ぶんをまとめて公開する。
 *
 * ⚠️ **日付ではなく `periodId` で指定する**（代表判断 2026-09-02）。
 * 日付で切ると期間の一部だけ公開した状態＝「一部公開」が作れてしまう。
 *
 * ⚠️ **シフト提出を締め切ってからでないと通らない**（`409 SUBMISSION_NOT_CLOSED`）。
 */
export const publishShifts = async (
  periodId: number
): Promise<{ published: number; filledDayOff: number }> => {
  const r = await api.post('/shifts/publish', { periodId });
  return r.data;
};

/**
 * 公開を取り消す。
 *
 * ⚠️ **公開のときに自動で入った公休は消える**（「未定」に戻る)。
 * 手で置いた公休は残る。取り消すと従業員の画面からは消え、予実では下書き扱いになる。
 * **受付は自動で再開しない**（再開は手で選ぶ）。
 */
export const unpublishShifts = async (
  periodId: number
): Promise<{ unpublished: number; removedDayOff: number }> => {
  const r = await api.post('/shifts/unpublish', { periodId });
  return r.data;
};

/** 予定と実績の突合。**評価の言葉は返らない**（事実だけ） */
export type DayStatus =
  | 'matched'
  | 'missing_punch'
  | 'unplanned'
  | 'worked_on_day_off'
  | 'count_mismatch'
  | 'day_off';

export const STATUS_LABELS: Record<DayStatus, string> = {
  matched: '予定どおり',
  missing_punch: '打刻なし',
  unplanned: '予定外の出勤',
  worked_on_day_off: '公休日に打刻',
  count_mismatch: '回数が違う',
  day_off: '公休',
};

export interface ComparisonItem {
  userId: number;
  date: string;
  status: DayStatus;
  plannedCount: number;
  actualCount: number;
  plannedMinutes: number;
  actualMinutes: number;
  pairs: {
    plannedStart: string | null;
    actualStart: string | null;
    startDiffMinutes: number | null;
    endDiffMinutes: number | null;
  }[];
  /**
   * **下書き（未確定）のシフトがその日にあるか。**
   * 突合(status)には使わないが、帯としては描く。
   */
  hasDraft?: boolean;
  /** 下書きの帯。描画用（その日の 0:00 JST からの分） */
  draftRanges?: { start: number; end: number }[];
  /** 描画用。**その日の 0:00(JST) からの分**。翌日にまたぐ枠は 1440 を超える */
  plannedRanges: { start: number; end: number }[];
  /** 退勤が未打刻なら `end` は null */
  actualRanges: { start: number; end: number | null }[];
}

export const compareShifts = async (
  from: string,
  to: string,
  userId?: number
): Promise<{ items: ComparisonItem[]; summary: Record<string, number> }> => {
  const r = await api.get('/shifts/comparison', { params: { from, to, userId } });
  return r.data;
};
